import { create } from 'zustand'
import { stopMetrika } from '@/lib/metrika'
import { ApiError } from '@/lib/api'
import { authApi, refreshSession, tokens, type CurrentUser, type TokenPair } from '@/lib/auth'

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated'

interface AuthState {
  status: AuthStatus
  user: CurrentUser | null
  /** Восстанавливает сессию по сохранённому refresh-токену при загрузке. */
  bootstrap: () => Promise<void>
  /** Сохраняет выданную пару токенов после входа или регистрации. */
  setSession: (pair: TokenPair) => void
  /** Обновляет данные текущего пользователя (смена номера, 2FA, профиль). */
  setUser: (user: CurrentUser) => void
  patchUser: (patch: Partial<CurrentUser>) => void
  /** Порядок серверов слева — меняется перетаскиванием, на сервер уходит отдельным запросом. */
  setGuildOrder: (guilds: string[]) => void
  signOut: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  user: null,
  bootstrap: async () => {
    if (!tokens.refresh()) {
      set({ status: 'anonymous', user: null })
      return
    }
    try {
      const pair = await refreshSession()
      set({ status: 'authenticated', user: pair.user })
    } catch (err) {
      // сеть недоступна — не выкидываем пользователя, токены остаются
      if (err instanceof ApiError && err.status === 401) tokens.clear()
      set({ status: 'anonymous', user: null })
    }
  },
  setSession: (pair) => {
    stopMetrika()
    tokens.save(pair)
    set({ status: 'authenticated', user: pair.user })
  },
  setUser: (user) => set({ user }),
  patchUser: (patch) => set((s) => (s.user ? { user: { ...s.user, ...patch } } : {})),
  setGuildOrder: (guilds) => set((s) => (s.user ? { user: { ...s.user, guild_order: guilds } } : {})),
  signOut: async () => {
    try {
      await authApi.logout()
    } catch {
      /* сессия могла уже истечь */
    }
    tokens.clear()
    set({ status: 'anonymous', user: null })
  },
}))
